import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { database } from '../../firebaseConfig';
import { FiUser, FiPhone, FiSave, FiMap, FiGlobe, FiX, FiBook } from 'react-icons/fi';
import Select from 'react-select';

// Static list of countries with flags
const countries = [
  { label: 'Pakistan', value: 'PK', flag: 'pk' },
  { label: 'United States', value: 'US', flag: 'us' },
  { label: 'Canada', value: 'CA', flag: 'ca' },
  { label: 'India', value: 'IN', flag: 'in' },
  { label: 'Germany', value: 'DE', flag: 'de' },
  { label: 'Australia', value: 'AU', flag: 'au' },
  { label: 'United Kingdom', value: 'GB', flag: 'gb' },
  { label: 'France', value: 'FR', flag: 'fr' },
  { label: 'Italy', value: 'IT', flag: 'it' },
  { label: 'Japan', value: 'JP', flag: 'jp' },
  { label: 'Brazil', value: 'BR', flag: 'br' },
  { label: 'South Korea', value: 'KR', flag: 'kr' },
  { label: 'Mexico', value: 'MX', flag: 'mx' },
  { label: 'Spain', value: 'ES', flag: 'es' },
  { label: 'Russia', value: 'RU', flag: 'ru' },
  { label: 'China', value: 'CN', flag: 'cn' },
  { label: 'South Africa', value: 'ZA', flag: 'za' },
  // Add more countries as necessary
];

// Preparing for options
const preparingForOptions = [
  { label: 'Engineering / Computer Science / BS Mathematics (With Chemistry)', value: 'engineering_cs_math_chemistry' },
  { label: 'Engineering / Computer Science / BS Mathematics (With Computer Science)', value: 'engineering_cs_math_cs' },
  { label: 'BS Biotechnology / BS Environmental Science', value: 'biotech_env_science' },
  { label: 'Business Studies & Social Sciences', value: 'business_social_sciences' },
  { label: 'Bachelor of Architecture / Industrial Design', value: 'architecture_industrial_design' },
  { label: 'BS Mathematics', value: 'bs_mathematics' },
];

// Custom styles for react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderWidth: '2px',
    borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
    boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
    borderRadius: '0.5rem',
    padding: '0.25rem',
    '&:hover': {
      borderColor: '#3B82F6'
    }
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#DBEAFE' : 'white',
    color: state.isSelected ? 'white' : '#374151',
    padding: '0.75rem',
    cursor: 'pointer'
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
  })
};

const ProfileForm = ({ user, setIsProfileIncomplete }) => {
  const [name, setName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [preparingFor, setPreparingFor] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formStep, setFormStep] = useState(1); // For multi-step form

  // Load the existing user profile if available
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const userRef = doc(database, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.personalInfo) {
            const { name, country, city, phoneNumber, preparingFor } = userData.personalInfo;
            setName(name || '');
            setPhoneNumber(phoneNumber || '');
            setSelectedCountry(countries.find((c) => c.value === country) || null);
            setSelectedCity(city || '');
            setPreparingFor(preparingForOptions.find((p) => p.value === preparingFor) || null);
          }

          // Check if profile is already completed
          if (userData.profileCompleted) {
            setIsProfileIncomplete(false); // Profile is completed
          }
        }
      } catch (err) {
        setError('Error loading user profile data.');
        console.error('Error loading user profile:', err);
      } finally {
        setIsLoading(false); // Stop loading once data is fetched
      }
    };

    if (user) {
      loadUserProfile();
    }
  }, [user, setIsProfileIncomplete]);

  // Handle country selection change
  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
    setSelectedCity(''); // Reset city when country changes
  };

  // Move to next step
  const goToNextStep = () => {
    if (formStep === 1 && !name) {
      setError('Please enter your name to continue');
      return;
    }
    if (formStep === 2 && !preparingFor) {
      setError('Please select what you are preparing for');
      return;
    }
    setError(null);
    if (formStep === 1) {
      setFormStep(2);
    } else if (formStep === 2) {
      setFormStep(3);
    }
  };

  // Move to previous step
  const goToPrevStep = () => {
    setError(null);
    if (formStep === 3) {
      setFormStep(2);
    } else if (formStep === 2) {
      setFormStep(1);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate the form data
    if (!name || !selectedCountry || !selectedCity || !phoneNumber || !preparingFor) {
      setError('Please fill out all fields.');
      return;
    }

    try {
      const userRef = doc(database, 'users', user.uid);

      // Update profile data and mark profile as completed
      await updateDoc(userRef, {
        personalInfo: {
          name,
          country: selectedCountry.value,
          city: selectedCity,
          phoneNumber,
          preparingFor: preparingFor.value,
        },
        profileCompleted: true, // Mark profile as complete
      });

      // Mark the profile as complete and remove the form
      setIsProfileIncomplete(false);
    } catch (err) {
      setError('Error saving profile data.');
      console.error('Error updating user profile:', err);
    }
  };

  // If loading, show loading spinner
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="mt-4 text-xl font-medium text-gray-700">Loading your profile...</div>
        </div>
      </div>
    );
  }

  return (
    // Full screen overlay to hide other components
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-70 backdrop-blur-sm z-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden relative">
        {/* Progress indicator */}
        <div className="w-full bg-gray-200 h-1">
          <div 
            className="bg-blue-600 h-full transition-all duration-300"
            style={{ width: formStep === 1 ? '33%' : formStep === 2 ? '66%' : '100%' }}
          ></div>
        </div>
        
        {/* Form header */}
        <div className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white px-6 py-5">
          <h2 className="text-2xl font-bold">Complete Your Profile</h2>
          <p className="text-blue-100 mt-1">
            {formStep === 1 ? 'Let\'s start with your name' : formStep === 2 ? 'What are you preparing for?' : 'Just a few more details'}
          </p>
        </div>
        
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
            <p className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
              </svg>
              {error}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-6 py-5">
          {formStep === 1 ? (
            // Step 1: Name input
            <div className="space-y-6">
              <div className="relative">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiUser className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    id="name"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <FiUser className="text-blue-600 text-xl" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    We'll use your name to personalize your experience and for communication purposes.
                  </p>
                </div>
              </div>
            </div>
          ) : formStep === 2 ? (
            // Step 2: Preparing for
            <div className="space-y-6">
              <div>
                <label htmlFor="preparingFor" className="block text-sm font-medium text-gray-700 mb-1">
                  Preparing For <span className="text-red-500">*</span>
                </label>
                <Select
                  id="preparingFor"
                  options={preparingForOptions}
                  value={preparingFor}
                  onChange={setPreparingFor}
                  styles={customSelectStyles}
                  placeholder="Select what you are preparing for"
                  isSearchable
                />
              </div>
              
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <FiBook className="text-blue-600 text-xl" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    This helps us recommend relevant mock tests and questions tailored to your field of study.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Step 3: Location and contact details
            <div className="space-y-5">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <Select
                  id="country"
                  options={countries}
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  styles={customSelectStyles}
                  getOptionLabel={(e) => (
                    <div className="flex items-center">
                      <img src={`https://flagcdn.com/w20/${e.flag}.png`} alt={e.label} className="w-6 h-4 mr-2" />
                      {e.label}
                    </div>
                  )}
                  getOptionValue={(e) => e.value}
                  placeholder="Select your country"
                  isSearchable
                />
              </div>

              <div className="relative">
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiMap className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    id="city"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    placeholder="Enter your city"
                    required
                  />
                </div>
              </div>

              <div className="relative">
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiPhone className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    id="phoneNumber"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            {formStep === 1 || formStep === 2 ? (
              <button
                type="button"
                onClick={goToNextStep}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg flex items-center justify-center hover:bg-blue-700 transition duration-300 font-medium"
              >
                Continue
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={goToPrevStep}
                  className="bg-gray-200 text-gray-800 px-4 py-3 rounded-lg flex items-center justify-center hover:bg-gray-300 transition duration-300 font-medium w-1/3"
                >
                  <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  Back
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-3 rounded-lg flex items-center justify-center hover:bg-blue-700 transition duration-300 font-medium w-2/3 ml-3"
                >
                  <FiSave className="mr-2" />
                  Complete Profile
                </button>
              </>
            )}
          </div>
        </form>
        
        {/* Bottom info section */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <p className="text-sm text-gray-600 flex items-start">
            <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
            </svg>
            Your information helps us personalize your experience. We value your privacy and will never share your data without permission.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileForm;